import React from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import Header from "../components/Header";

const Section = ({ title, children }) => (
  <section className="mb-9">
    <h2 className="text-xl font-medium text-[#2C2123] mb-3">{title}</h2>
    <div className="text-[#3C2F33] leading-relaxed space-y-3">{children}</div>
  </section>
);

const Privacy = () => {
  const { t, i18n } = useTranslation();
  const isNonEnglish = (i18n.language || "en").split("-")[0] !== "en";
  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main
        className="pt-32 pb-24 px-6 max-w-3xl mx-auto"
        data-testid="privacy-page"
      >
        <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
          {t("privacy.kicker")}
        </span>
        <h1 className="text-4xl font-light text-[#2C2123] leading-tight mb-2">
          {t("privacy.headline")}
        </h1>
        <p className="text-[#8A7D82] mb-6 text-sm">
          {t("privacy.lastUpdated")}: August 2026
        </p>

        {/* For FR/ES users we render the full policy translated, plus a
            small convenience-translation disclaimer at the top: the
            English version remains the legally-governing text. This is
            the industry-standard pattern for multi-locale privacy
            policies without per-jurisdiction legal review. */}
        {isNonEnglish && (
          <div
            className="mb-8 rounded-2xl bg-[#FDF4EF] border border-[#EADDD7] px-4 py-3 text-sm text-[#5C4B51]"
            data-testid="privacy-translation-note"
          >
            <Trans
              i18nKey="privacyBody.translationNote"
              components={{
                a: (
                  <a
                    href="mailto:hello@trubeaute.com"
                    className="text-[#9B6A6C] underline"
                  />
                ),
              }}
            />
          </div>
        )}

        <Section title={t("privacyBody.whatWeCollectTitle")}>
          <ul className="list-disc list-inside space-y-1">
            <li>{t("privacyBody.whatWeCollect1")}</li>
            <li>{t("privacyBody.whatWeCollect2")}</li>
            <li>{t("privacyBody.whatWeCollect3")}</li>
            <li>{t("privacyBody.whatWeCollect4")}</li>
          </ul>
        </Section>

        <Section title={t("privacyBody.photoRetentionTitle")}>
          <p>
            <Trans
              i18nKey="privacyBody.photoRetentionBody"
              components={{ b: <strong /> }}
            />
          </p>
        </Section>

        <Section title={t("privacyBody.howWeUseTitle")}>
          <ul className="list-disc list-inside space-y-1">
            <li>{t("privacyBody.howWeUse1")}</li>
            <li>{t("privacyBody.howWeUse2")}</li>
            <li>{t("privacyBody.howWeUse3")}</li>
            <li>{t("privacyBody.howWeUse4")}</li>
          </ul>
        </Section>

        <Section title={t("privacyBody.affiliateTitle")}>
          <p>{t("privacyBody.affiliateBody")}</p>
        </Section>

        <Section title={t("privacyBody.processorsTitle")}>
          <ul className="list-disc list-inside space-y-1">
            <li>{t("privacyBody.processor1")}</li>
            <li>{t("privacyBody.processor2")}</li>
            <li>{t("privacyBody.processor3")}</li>
            <li>{t("privacyBody.processor4")}</li>
            <li>{t("privacyBody.processor5")}</li>
            <li>{t("privacyBody.processor6")}</li>
          </ul>
        </Section>

        <Section title={t("privacyBody.rightsTitle")}>
          <p>
            <Trans
              i18nKey="privacyBody.rightsBody"
              components={{
                a: (
                  <a
                    href="mailto:hello@trubeaute.com"
                    className="text-[#9B6A6C] underline"
                  />
                ),
              }}
            />
          </p>
        </Section>

        <Section title={t("privacyBody.cookiesTitle")}>
          <p>{t("privacyBody.cookiesBody")}</p>
        </Section>

        <Section title={t("privacyBody.ageTitle")}>
          <p>
            <Trans
              i18nKey="privacyBody.ageBody"
              components={{
                b: <strong />,
                termsLink: (
                  <Link to="/terms" className="text-[#9B6A6C] underline" />
                ),
              }}
            />
          </p>
        </Section>
      </main>
    </div>
  );
};

export default Privacy;
